import { lazy, Suspense, useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import ScorerTransferNotification from "./components/ScorerTransferNotification";
import BottomNav from "./components/BottomNav";
import { isAuctionOrSeasonRoute } from "./lib/bottomNavVisibility";
import { ConfirmProvider } from "./components/ConfirmDialog";
import { BrandingProvider } from "./lib/branding";
import { AppErrorBoundary, NetworkStatus } from "./components/MobileExperience";
import { NativeMobileRuntime } from "./components/NativeMobileRuntime";

// Pages
const LandingPage = lazy(() => import("./pages/LandingPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CricketFactsPage = lazy(() => import("./pages/CricketFactsPage"));
const TeamsPage = lazy(() => import("./pages/TeamsPage"));
const PlayersPage = lazy(() => import("./pages/PlayersPage"));
const MatchesPage = lazy(() => import("./pages/MatchesPage"));
const MatchScoring = lazy(() => import("./pages/MatchScoring"));
const CreateMatchPage = lazy(() => import("./pages/CreateMatchPage"));
const MatchView = lazy(() => import("./pages/MatchView"));
const BirthdayPostPage = lazy(() => import("./pages/BirthdayPostPage"));
const TournamentsPage = lazy(() => import("./pages/TournamentsPage"));
const TournamentDetailPage = lazy(() => import("./pages/TournamentDetailPage"));
const SeasonsPage = lazy(() => import("./pages/SeasonsPage"));
const SeasonDetailPage = lazy(() => import("./pages/SeasonDetailPage"));
const JoinPage = lazy(() => import("./pages/JoinPage"));
const StatsPage = lazy(() => import("./pages/StatsPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const TossPage = lazy(() => import("./pages/TossPage"));
const PlayerStatsPage = lazy(() => import("./pages/PlayerStatsPage"));
const SuperAdminPage = lazy(() => import("./pages/SuperAdminPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const HowToUsePage = lazy(() => import("./pages/HowToUsePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const LiveAuctionPage = lazy(() => import("./pages/LiveAuctionPage"));
const AuctionEventsPage = lazy(() => import("./pages/AuctionEventsPage"));
const AuctionEventDetailPage = lazy(() => import("./pages/AuctionEventDetailPage"));
const ScoreOverlay = lazy(() => import("./pages/ScoreOverlay"));
const OverlaySetup = lazy(() => import("./pages/OverlaySetup"));
const LiveBroadcastPage = lazy(() => import("./pages/LiveBroadcastPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const ShopMartPage = lazy(() => import("./pages/ShopMartPage"));
const VendorOnboardingPage = lazy(() => import("./pages/VendorOnboardingPage"));
const VendorDashboardPage = lazy(() => import("./pages/VendorDashboardPage"));
const AdminShopMartPage = lazy(() => import("./pages/AdminShopMartPage"));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage"));
const SellOnCrickHerePage = lazy(() => import("./pages/SellOnCrickHerePage"));

// Theme support
import { ThemeProvider } from "./theme/ThemeProvider";
import "./theme/theme-light.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppLoadingScreen = () => (
  <div className="flex min-h-screen items-center justify-center bg-[#020617] px-6" role="status" aria-live="polite">
    <div className="flex flex-col items-center gap-4">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-700 border-t-[#CCFF00]" />
      <span className="text-sm font-medium text-slate-300">Loading CrickHere</span>
    </div>
  </div>
);

// Global axios config - withCredentials for cookie support
axios.defaults.withCredentials = true;

// Axios interceptors for mobile auth (cookies don't work reliably on mobile/PWA/Capacitor)
// Response interceptor: save session_token and user data from auth responses
axios.interceptors.response.use((response) => {
  const token = response.data?.session_token;
  if (token) {
    localStorage.setItem("session_token", token);
    // Also set default header for all future requests
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }
  // Save user data on auth responses (but not heartbeat which returns minimal data)
  if (response.data?.user_id && response.config.url?.includes("/auth/") && !response.config.url?.includes("/heartbeat")) {
    const userData = { ...response.data };
    delete userData.session_token; // Don't store token in user data
    localStorage.setItem("crickhere_user", JSON.stringify(userData));
  }
  // Clear on logout
  if (response.config.url?.includes("/auth/logout")) {
    localStorage.removeItem("session_token");
    localStorage.removeItem("crickhere_user");
    delete axios.defaults.headers.common["Authorization"];
  }
  return response;
}, async (error) => {
  const config = error.config;
  const isReadRequest = String(config?.method || "get").toLowerCase() === "get";
  const isTransient = !error.response || error.response.status === 408 || error.response.status === 429 || error.response.status >= 500;
  if (config && isReadRequest && isTransient && (config.__retryCount || 0) < 2) {
    config.__retryCount = (config.__retryCount || 0) + 1;
    await new Promise((resolve) => setTimeout(resolve, 350 * (2 ** (config.__retryCount - 1))));
    return axios(config);
  }
  // Only clear auth on explicit logout, not on background refresh failures
  // The ProtectedRoute handles session expiry gracefully with cached data
  return Promise.reject(error);
});

// Request interceptor: attach Bearer token to all requests EXCEPT public auth endpoints
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("session_token");
  const publicPaths = ["/auth/login", "/auth/mobile/send-otp", "/auth/mobile/verify-otp",
    "/auth/mobile/send-otp-verify", "/auth/mobile/verify-otp-only",
    "/auth/register", "/auth/google"];
  const isPublic = publicPaths.some(p => config.url?.includes(p));
  if (token && !config.headers["Authorization"] && !isPublic) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// Restore token from localStorage on app load (for PWA/mobile persistence)
const savedToken = localStorage.getItem("session_token");
if (savedToken) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${savedToken}`;
}

// Auth Context
export const AuthContext = ({ children }) => {
  return children;
};

const getCachedAuthUser = () => {
  try {
    const token = localStorage.getItem("session_token");
    const rawUser = localStorage.getItem("crickhere_user");
    if (!token || !rawUser) return null;
    const user = JSON.parse(rawUser);
    if (!user || typeof user !== "object") return null;
    return { token, user };
  } catch {
    return null;
  }
};

const getDefaultAuthedPath = (user) => {
  if (user?.role === "auction_bidder" && user?.bidder_tournament_id) {
    return `/tournaments/${user.bidder_tournament_id}/auction`;
  }
  if (!user?.name || String(user.name).trim() === "") {
    return "/profile";
  }
  return "/dashboard";
};

const SessionAwareEntryRoute = () => {
  const cached = getCachedAuthUser();
  if (cached?.user) {
    return <Navigate to={getDefaultAuthedPath(cached.user)} replace />;
  }
  return <LandingPage />;
};

const INDEXABLE_ROUTES = new Set([
  "/", "/home", "/about", "/features", "/how-to-use", "/contact",
  "/pricing", "/privacy", "/terms", "/sell-on-crickhere",
]);

const SEO_TITLES = {
  "/": "CrickHere - Live Cricket Scoring & Tournament Management App",
  "/home": "CrickHere - Live Cricket Scoring & Tournament Management App",
  "/about": "About CrickHere - Digital Cricket Scoring Platform",
  "/features": "CrickHere Features - Live Scoring, Tournaments & Auctions",
  "/how-to-use": "How to Use CrickHere Cricket Scoring",
  "/contact": "Contact CrickHere",
  "/pricing": "CrickHere Pricing",
  "/privacy": "CrickHere Privacy Policy",
  "/terms": "CrickHere Terms of Service",
  "/sell-on-crickhere": "Sell Cricket Equipment on CrickHere",
};

function RouteSeoMeta() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname.replace(/\/$/, "") || "/";
    const isPublicMatch = /^\/match\/[^/]+$/.test(path);
    const isIndexable = INDEXABLE_ROUTES.has(path) || isPublicMatch;
    const canonicalPath = path === "/home" ? "/" : path;
    const canonicalUrl = `https://crickhere.in${canonicalPath}`;

    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", isIndexable
      ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      : "noindex, follow");

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", canonicalUrl);
    if (SEO_TITLES[path]) document.title = SEO_TITLES[path];
  }, [location.pathname]);

  return null;
}

const PublicAuthRoute = ({ children }) => {
  const cached = getCachedAuthUser();
  if (cached?.user) {
    return <Navigate to={getDefaultAuthedPath(cached.user)} replace />;
  }
  return children;
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get("session_id");
      const sessionToken = params.get("session_token");

      // Custom Google OAuth flow: backend directly issued a session_token
      if (sessionToken) {
        try {
          localStorage.setItem("session_token", sessionToken);
          axios.defaults.headers.common["Authorization"] = `Bearer ${sessionToken}`;
          // Fetch user profile with the token
          const me = await axios.get(`${API}/auth/me`);
          localStorage.setItem("crickhere_user", JSON.stringify(me.data));
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/dashboard", { state: { user: me.data } });
        } catch (error) {
          console.error("Google auth error:", error);
          toast.error("Authentication failed");
          navigate("/login");
        }
        return;
      }

      // Legacy Emergent session_id flow
      if (!sessionId) {
        navigate("/login");
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, 
          { session_id: sessionId },
          { withCredentials: true }
        );
        
        // Clear hash and navigate to dashboard with user data
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { state: { user: response.data } });
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Authentication failed");
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center">
      <div className="text-[#CCFF00] text-xl">Authenticating...</div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAuth = true }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [scorerTransferNotif, setScorerTransferNotif] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const lastNotifIdRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    let heartbeatInterval = null;
    
    const handleAuth = async () => {
      // If user data passed from AuthCallback, use it
      if (location.state?.user) {
        if (isMounted) {
          setUser(location.state.user);
          setIsAuthenticated(true);
          // Save to localStorage for persistence
          localStorage.setItem("crickhere_user", JSON.stringify(location.state.user));
          
          // Bidder auto-redirect to auction page
          if (location.state.user.role === "auction_bidder" && location.state.user.bidder_tournament_id) {
            navigate(`/tournaments/${location.state.user.bidder_tournament_id}/auction`, { replace: true });
            return;
          }
          
          // Only redirect to profile if mobile is missing
          // Check both 'mobile' and 'phone' fields, and 'has_phone' flag
          const userHasPhone = location.state.user.mobile || location.state.user.phone || location.state.user.has_phone;
          if (!userHasPhone && location.pathname !== "/profile") {
            navigate("/profile", { state: { forceComplete: true }, replace: true });
          }
        }
        return;
      }

      // Check localStorage for cached user (instant, no API call — crucial for mobile)
      const cachedUser = localStorage.getItem("crickhere_user");
      const cachedToken = localStorage.getItem("session_token");
      if (cachedUser && cachedToken) {
        try {
          const userData = JSON.parse(cachedUser);
          if (isMounted) {
            setUser(userData);
            setIsAuthenticated(true);
            
            // Bidder auto-redirect on cached login (reload/revisit)
            if (userData.role === "auction_bidder" && userData.bidder_tournament_id) {
              if (!location.pathname.includes("/auction")) {
                navigate(`/tournaments/${userData.bidder_tournament_id}/auction`, { replace: true });
              }
            }
          }
          // Background refresh — update user data silently, don't logout on failure
          axios.get(`${API}/auth/me`).then((response) => {
            if (isMounted && response.data) {
              setUser(response.data);
              localStorage.setItem("crickhere_user", JSON.stringify(response.data));
            }
          }).catch((err) => {
            // Session truly expired on server (401) — but only clear if it's a persistent failure
            if (err.response?.status === 401) {
              // Double-check: retry once after a short delay to handle transient failures
              setTimeout(async () => {
                try {
                  const retryRes = await axios.get(`${API}/auth/me`);
                  if (isMounted && retryRes.data) {
                    setUser(retryRes.data);
                    localStorage.setItem("crickhere_user", JSON.stringify(retryRes.data));
                  }
                } catch (retryErr) {
                  if (retryErr.response?.status === 401 && isMounted) {
                    // Confirmed session expired — clear and redirect
                    localStorage.removeItem("session_token");
                    localStorage.removeItem("crickhere_user");
                    delete axios.defaults.headers.common["Authorization"];
                    if (requireAuth) {
                      setIsAuthenticated(false);
                      navigate("/login");
                    }
                  }
                  // Network errors on retry: stay logged in with cached data
                }
              }, 3000);
            }
            // Network errors: silent fail — user stays logged in with cached data
          });
          return;
        } catch {
          // Invalid cached data, fall through to API check
        }
      }

      try {
        const response = await axios.get(`${API}/auth/me`);
        if (isMounted) {
          setUser(response.data);
          setIsAuthenticated(true);
          localStorage.setItem("crickhere_user", JSON.stringify(response.data));
          
          if (response.data.role === "auction_bidder" && response.data.bidder_tournament_id) {
            if (!location.pathname.includes("/auction")) {
              navigate(`/tournaments/${response.data.bidder_tournament_id}/auction`, { replace: true });
            }
            return;
          }
          
          // Only redirect to profile if mobile is missing
          // Check both 'mobile' and 'phone' fields
          const hasPhoneNumber = response.data.mobile || response.data.phone;
          if (!hasPhoneNumber && location.pathname !== "/profile") {
            navigate("/profile", { state: { forceComplete: true }, replace: true });
          }
        }
      } catch {
        if (isMounted) {
          // No cache and API failed — truly not authenticated
          localStorage.removeItem("session_token");
          localStorage.removeItem("crickhere_user");
          delete axios.defaults.headers.common["Authorization"];
          setIsAuthenticated(false);
          if (requireAuth) {
            navigate("/login");
          }
        }
      }
    };

    handleAuth();
    
    // Heartbeat to track active sessions - every 5 minutes
    const sendHeartbeat = async () => {
      try {
        await axios.post(`${API}/auth/heartbeat`, {}, { withCredentials: true });
      } catch {
        // Silently ignore heartbeat failures
      }
    };
    
    // Send initial heartbeat after auth check
    if (requireAuth) {
      setTimeout(() => {
        if (isMounted) sendHeartbeat();
      }, 2000);
      
      // Then send heartbeat every 5 minutes
      heartbeatInterval = setInterval(() => {
        if (isMounted) sendHeartbeat();
      }, 5 * 60 * 1000);
      
      // Check for scorer transfer notifications every 5 seconds
      const checkScorerTransferNotifications = async () => {
        try {
          const res = await axios.get(`${API}/notifications/pending`, { withCredentials: true });
          const notifications = res.data.notifications || [];
          
          // Find any scorer transfer notification that hasn't been shown yet
          const scorerNotif = notifications.find(n => 
            n.type === "scorer_transfer" && 
            n.notification_id !== lastNotifIdRef.current
          );
          
          if (scorerNotif) {
            lastNotifIdRef.current = scorerNotif.notification_id;
            
            // Show modal notification instead of toast
            setScorerTransferNotif(scorerNotif);
            
            // Mark as read
            await axios.post(`${API}/notifications/${scorerNotif.notification_id}/mark-read`, {}, { withCredentials: true });
          }
        } catch {
          // Silently ignore errors
        }
      };
      
      // Check every 5 seconds
      const notifInterval = setInterval(checkScorerTransferNotifications, 5000);
      // Also check immediately
      setTimeout(checkScorerTransferNotifications, 2000);
      
      return () => {
        isMounted = false;
        if (heartbeatInterval) clearInterval(heartbeatInterval);
        if (notifInterval) clearInterval(notifInterval);
      };
    }
    
    return () => {
      isMounted = false;
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
    };
  }, [navigate, location.state, requireAuth, location.pathname]);

  // Handler for dismissing scorer transfer notification
  const handleDismissNotif = () => {
    setScorerTransferNotif(null);
  };

  if (isAuthenticated === null && requireAuth) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="text-[#CCFF00] text-xl">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated && requireAuth) {
    return null;
  }

  // Onboarding lock: if the user just auto-registered via Firebase OTP (no name yet),
  // they MUST land on Profile and complete it. We never let them bounce away
  // until `user.name` is non-empty. The Profile page itself is exempt so they can fill it.
  const needsOnboarding = !!user && (!user.name || String(user.name).trim() === "");
  if (needsOnboarding && location.pathname !== "/profile") {
    return <Navigate to="/profile" replace state={{ forceComplete: true, mobile: user.mobile || user.phone || "" }} />;
  }

  const focusedRoutes = ["/create-match", "/scoring/", "/broadcast/", "/join/", "/toss", "/admin", "/shop-mart/admin"];
  const isTournamentDetailRoute = /^\/tournament\/[^/]+(?:\/.*)?$/.test(location.pathname);
  const isLiveAuctionRoute = /^\/tournaments\/[^/]+\/auction\/?$/.test(location.pathname);
  const isFocusedRoute = isTournamentDetailRoute || isLiveAuctionRoute || focusedRoutes.some((path) => location.pathname === path || location.pathname.startsWith(path));
  const showBottomNav = !needsOnboarding && user?.role !== "auction_bidder" && !isFocusedRoute && !isAuctionOrSeasonRoute(location.pathname);

  return (
    <>
      <div
        className={`protected-route-scroll h-[100dvh] min-h-0 overflow-x-hidden overflow-y-auto overscroll-y-contain ${showBottomNav ? "pb-24 md:pb-0" : ""}`}
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {children({ user, setUser })}
      </div>
      {showBottomNav && <BottomNav user={user} />}
      <ScorerTransferNotification 
        notification={scorerTransferNotif} 
        onDismiss={handleDismissNotif}
      />
    </>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.querySelector(".protected-route-scroll")?.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname]);

  // Check URL fragment for session_id or session_token synchronously
  if (location.hash?.includes("session_id=") || location.hash?.includes("session_token=")) {
    return <AuthCallback />;
  }

  // ONBOARDING LOCK: if a logged-in user has not completed their profile
  // (no name yet — typically a brand-new Firebase OTP auto-register), trap
  // them on /profile no matter which URL they try to open. This applies to
  // PUBLIC routes too (landing, about, etc.) so they can't escape via the navbar.
  try {
    const cached = getCachedAuthUser();
    if (cached?.user) {
      const u = cached.user;
      const needsOnboarding = u && (!u.name || String(u.name).trim() === "");
      const allowedPaths = ["/profile", "/logout"];
      const isAllowed = allowedPaths.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
      if (needsOnboarding && !isAllowed) {
        return <Navigate to="/profile" replace state={{ forceComplete: true, mobile: u.mobile || u.phone || "" }} />;
      }
    }
  } catch { /* ignore parse errors */ }

  return (
    <Suspense fallback={<AppLoadingScreen />}>
    <Routes>
      <Route path="/" element={<SessionAwareEntryRoute />} />
      <Route path="/home" element={<LandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/how-to-use" element={<HowToUsePage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/login" element={<PublicAuthRoute><LoginPage /></PublicAuthRoute>} />
      <Route path="/register" element={<PublicAuthRoute><RegisterPage /></PublicAuthRoute>} />
      <Route path="/sell-on-crickhere" element={<SellOnCrickHerePage />} />
      <Route path="/match/:matchId" element={<MatchView />} />
      <Route path="/overlay/:matchId" element={<ScoreOverlay />} />
      <Route path="/overlay-setup/:matchId" element={<OverlaySetup />} />
      <Route
        path="/broadcast/:matchId"
        element={
          <ProtectedRoute>
            {() => <LiveBroadcastPage />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/birthday/:post_id"
        element={
          <ProtectedRoute>
            {() => <BirthdayPostPage />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <Dashboard user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/cricket-facts"
        element={
          <ProtectedRoute>
            {() => <CricketFactsPage />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams"
        element={
          <ProtectedRoute>
            {({ user }) => <TeamsPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/players"
        element={
          <ProtectedRoute>
            {({ user }) => <PlayersPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/matches"
        element={
          <ProtectedRoute>
            {({ user }) => <MatchesPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-match"
        element={
          <ProtectedRoute>
            {({ user }) => <CreateMatchPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/scoring/:matchId"
        element={
          <ProtectedRoute>
            {({ user }) => <MatchScoring user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/tournaments"
        element={
          <ProtectedRoute>
            {({ user }) => <TournamentsPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/tournament/:tournamentId"
        element={
          <ProtectedRoute>
            {({ user }) => <TournamentDetailPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route path="/seasons" element={<ProtectedRoute>{() => <SeasonsPage />}</ProtectedRoute>} />
      <Route path="/seasons/:seasonId" element={<ProtectedRoute>{({ user }) => <SeasonDetailPage user={user} />}</ProtectedRoute>} />
      <Route
        path="/stats"
        element={
          <ProtectedRoute>
            {({ user }) => <StatsPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/toss"
        element={
          <ProtectedRoute>
            {({ user }) => <TossPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/players/:playerId/stats"
        element={
          <ProtectedRoute>
            {({ user }) => <PlayerStatsPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <ProfilePage user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            {({ user }) => <SuperAdminPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/join/:type/:inviteCode"
        element={
          <ProtectedRoute requireAuth={false}>
            {({ user }) => <JoinPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/auctions"
        element={
          <ProtectedRoute>
            {({ user }) => <AuctionEventsPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/auction/:eventId"
        element={
          <ProtectedRoute>
            {({ user }) => <AuctionEventDetailPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/tournaments/:tournamentId/auction"
        element={
          <ProtectedRoute requireAuth={false}>
            {({ user }) => <LiveAuctionPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/shop-mart"
        element={
          <ProtectedRoute>
            {({ user }) => <ShopMartPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/shop-mart/sell"
        element={
          <ProtectedRoute requireAuth={false}>
            {({ user }) => <VendorOnboardingPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/shop-mart/vendor"
        element={
          <ProtectedRoute>
            {({ user }) => <VendorDashboardPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/shop-mart/admin"
        element={
          <ProtectedRoute>
            {({ user }) => <AdminShopMartPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/shop-mart/product/:productId"
        element={
          <ProtectedRoute>
            {({ user }) => <ProductDetailPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="light">
      <div className="App min-h-screen bg-[#020617]">
        <Toaster
          position="top-center"
          visibleToasts={1}
          duration={3200}
          closeButton
          richColors
          className="crickhere-feedback-center"
          toastOptions={{ className: "crickhere-feedback-message" }}
        />
        <NetworkStatus />
        <AppErrorBoundary>
          <BrowserRouter>
            <RouteSeoMeta />
            <BrandingProvider>
              <ConfirmProvider>
                <NativeMobileRuntime />
                <AppRouter />
              </ConfirmProvider>
            </BrandingProvider>
          </BrowserRouter>
        </AppErrorBoundary>
      </div>
    </ThemeProvider>
  );
}

export default App;
